import { Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Outlet, Link, NavLink, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Clapperboard, FlaskConical, FolderOpen, Menu, Sparkles, Upload, Video, X } from 'lucide-react';
import { ErrorBoundary } from './ErrorBoundary';
import { Logo } from './ui/Logo';
import { UserMenu } from './UserMenu';
import { SEOHead } from './SEO';
import './page-transitions.css';

interface LayoutProps { hideFooter?: boolean; fullscreen?: boolean; }
const navigation = [
  { to: '/replays', label: 'Explore replays', description: 'Step into the game. Find your angle.', icon: Video },
  { to: '/clips', label: 'Clips', description: 'The moments worth another look.', icon: Clapperboard },
  { to: '/rank-lab', label: 'Rank Lab', description: 'Try the experimental rank estimator.', icon: FlaskConical },
  { to: '/news', label: 'What’s new', description: 'The latest from BallCam.', icon: Sparkles },
];
const footerGroups = [
  { title: 'Make it yours', links: [['Explore replays', '/replays'], ['Discover clips', '/clips'], ['Upload a replay', '/upload'], ['Your library', '/my-replays']] },
  { title: 'Around the game', links: [['Rank Lab', '/rank-lab'], ['What’s new', '/news'], ['Ideas & feedback', '/feedback'], ['Replay quality', '/faq/replay-quality'], ['Cheat reports', '/cheaters']] },
  { title: 'Get in touch', links: [['Contact', '/contact'], ['API documentation', '/docs/api'], ['Privacy', '/privacy'], ['Legal notice', '/legal']] },
];

export function Layout({ hideFooter = false, fullscreen = false }: LayoutProps) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const isHome = location.pathname === '/';
  const isThumbnail = searchParams.get('thumbnail') === 'true';

  useLayoutEffect(() => {
    setMobileMenuOpen(false);
    if (location.hash) {
      const timer = setTimeout(() => document.getElementById(location.hash.slice(1))?.scrollIntoView(), 100);
      return () => clearTimeout(timer);
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const wideScreen = window.matchMedia('(min-width: 1024px)');
    const closeOnDesktop = () => { if (wideScreen.matches) setMobileMenuOpen(false); };
    wideScreen.addEventListener('change', closeOnDesktop);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    menuRef.current?.querySelector<HTMLElement>('a')?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setMobileMenuOpen(false); menuButtonRef.current?.focus(); }
      if (event.key === 'Tab') {
        const targets = [menuButtonRef.current, ...Array.from(menuRef.current?.querySelectorAll<HTMLElement>('a, button') || [])].filter(Boolean) as HTMLElement[];
        const first = targets[0], last = targets[targets.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { wideScreen.removeEventListener('change', closeOnDesktop); document.body.style.overflow = previousOverflow; document.removeEventListener('keydown', onKey); };
  }, [mobileMenuOpen]);

  return (
    <div className={`bc-shell ${fullscreen ? 'bc-shell-viewer' : ''}`}>
      <SEOHead title="Rocket League replays. A whole new angle." description="Relive Rocket League in 3D. Explore every angle, watch with friends, and turn your best moments into shareable clips." />
      {!isThumbnail && <>
        <a className="bc-skip" href="#main-content">Skip to content</a>
        <header className={`bc-nav ${isHome ? 'bc-nav-home' : ''}`}>
          <div className="bc-container bc-nav-inner">
            <Link to="/" aria-label="BallCam.tv home"><Logo /></Link>
            <nav className="bc-nav-links" aria-label="Main navigation">
              {navigation.map(item => <NavLink key={item.to} to={item.to} className="bc-nav-link">{item.label}</NavLink>)}
            </nav>
            <div className="bc-nav-actions">
              <Link to="/upload" className="bc-button bc-button-primary"><Upload size={15} /> Upload replay</Link>
              <UserMenu />
            </div>
            <div className="bc-mobile-nav-actions">
            <Link to="/upload" className="bc-mobile-upload" aria-label="Upload a replay"><Upload size={18} /></Link>
            <button ref={menuButtonRef} className="bc-menu-toggle" aria-label={mobileMenuOpen ? 'Close navigation' : 'Open navigation'} aria-expanded={mobileMenuOpen} aria-controls="mobile-navigation" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            </div>
          </div>
        </header>
        {mobileMenuOpen && <div ref={menuRef} id="mobile-navigation" className="bc-mobile-menu">
          <p className="bc-mobile-menu-label">A different perspective</p>
          <nav className="bc-mobile-main-links" aria-label="Mobile navigation">
            {navigation.map(({ to, label, description, icon: Icon }) => <NavLink key={to} to={to}><Icon size={21} strokeWidth={1.6} /><span><strong>{label}</strong><small>{description}</small></span><ArrowUpRight size={17} /></NavLink>)}
          </nav>
          <div className="bc-mobile-library-links">
            <Link to="/upload" className="bc-button bc-button-primary"><Upload size={17} /> Upload a replay <ArrowRight size={16} /></Link>
            <Link to="/my-replays" className="bc-button bc-button-secondary"><FolderOpen size={17} /> Your library</Link>
          </div>
          <UserMenu mobile onAction={() => setMobileMenuOpen(false)} />
        </div>}
      </>}
      <main id="main-content" tabIndex={-1} className={fullscreen ? 'bc-main-fullscreen' : isHome ? 'bc-main-home' : 'bc-main'}>
        <Suspense fallback={<div className="bc-route-loader" role="status">Opening BallCam…</div>}>
          <ErrorBoundary key={location.pathname}>
            {fullscreen ? <Outlet /> : <div key={location.pathname} className="bc-page-transition"><Outlet /></div>}
          </ErrorBoundary>
        </Suspense>
      </main>
      {!hideFooter && !fullscreen && <footer className="bc-footer">
        <div className="bc-container">
          <div className="bc-footer-top">
            <div>
              <Link to="/" aria-label="BallCam.tv home"><Logo /></Link>
              <p className="bc-footer-copy">A new perspective on the game you love.<br />Replay it. Make it yours. Share the moment.</p>
              <a href="https://github.com/MarlBurroW" target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 text-xs text-gray-400">Made by MarlBurroW <ArrowUpRight size={12} /></a>
            </div>
            {footerGroups.map(group => <div key={group.title}><h4>{group.title}</h4><ul>{group.links.map(([label, to]) => <li key={to}><Link to={to}>{label}</Link></li>)}</ul></div>)}
          </div>
          <div className="bc-footer-bottom">
            <span>© {new Date().getFullYear()} BallCam.tv · Every angle. Every play.</span>
            <span>3D meshes by Kartal · Quality by TitaniteChuck</span>
            <span>Community project. Not affiliated with Psyonix or Epic Games.</span>
          </div>
        </div>
      </footer>}
    </div>
  );
}
